import * as React from 'react';
import { Link, useMatch } from 'react-router-dom';
import { usePublicAppConfig } from '../contexts/PublicAppConfigContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { LiveMeetingBadge } from '../features/live-meeting/components/LiveMeetingBadge';
import { ModuleSelector } from '../features/modules/components/ModuleSelector';
import { salesApi } from '../features/sales/services/salesApi';
import { useProtectedImageUrl } from '../hooks/useProtectedImageUrl';
import { NotificationBell } from './NotificationBell';
import { UserMenu } from './UserMenu';
import { WorkspaceSelector } from './WorkspaceSelector';

export interface GlobalHeaderProps {
  /**
   * Label shown after the separator. Defaults to the workspace name.
   * Pass a string to override (e.g. "Settings" for the settings page).
   */
  identity?: React.ReactNode;
  /** Show the live meeting badge. Default: true. */
  showBadge?: boolean;
  /** Show the module selector. Default: true. */
  showModuleSelector?: boolean;
}

/**
 * GlobalHeader - shared top bar rendered by AppShell when no custom header is provided.
 *
 * Reads its own data via context hooks so pages don't need to pass wordmark,
 * workspace name, or module selector state as props.
 */
export function GlobalHeader({
  identity,
  showBadge = true,
  showModuleSelector = true,
}: GlobalHeaderProps) {
  const dealMatch = useMatch('/so/deal/:dealUuid');
  const dealEditMatch = useMatch('/so/deal/:dealUuid/edit');
  const rawDealUuid = dealMatch?.params.dealUuid ?? dealEditMatch?.params.dealUuid ?? null;
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const dealUuid = rawDealUuid && UUID_RE.test(rawDealUuid) ? rawDealUuid : null;

  const [fillingSampleData, setFillingSampleData] = React.useState(false);
  const handleFillSampleData = React.useCallback(async () => {
    if (!dealUuid) return;
    setFillingSampleData(true);
    try {
      const { answers: sampleAnswers } = await salesApi.getSampleDealData(dealUuid);
      const { questionnaire } = await salesApi.getDealQuestionnaire(dealUuid);

      const sampleMap = new Map<string, any[]>();
      for (const ans of sampleAnswers) {
        if (!sampleMap.has(ans.fieldKey)) sampleMap.set(ans.fieldKey, []);
        sampleMap.get(ans.fieldKey)!.push(ans.value);
      }

      const payload: any[] = [];
      const processFields = (fields: any[]) => {
        for (const field of fields) {
          const values = sampleMap.get(field.fieldKey);
          if (values && values.length > 0) {
            payload.push({
              templateFieldId: field.templateFieldId,
              fieldKey: field.fieldKey,
              value: values.shift(),
            });
          }
        }
      };

      for (const section of questionnaire.sections) {
        for (const group of section.groups) {
          processFields(group.fields);
        }
      }
      processFields(questionnaire.headerFields);

      // Append any remaining sample answers that didn't match (e.g. missing from template)
      for (const [fieldKey, values] of sampleMap.entries()) {
        for (const value of values) {
          payload.push({ fieldKey, value });
        }
      }

      await salesApi.saveDealQuestionnaire(dealUuid, payload);
      window.location.reload();
    } catch (e) {
      console.error('[GlobalHeader] fill sample data failed', e);
      setFillingSampleData(false);
    }
  }, [dealUuid]);
  const {
    config: { headerWordmark },
  } = usePublicAppConfig();
  const {
    workspace,
    isLoadingWorkspace,
    modules,
    activeModuleKey,
    setActiveModuleKey,
    availableWorkspaces,
    switchWorkspace,
  } = useWorkspace();

  const identityLabel = identity ?? (isLoadingWorkspace ? '...' : (workspace?.name ?? 'Workspace'));

  const logoUrl = workspace?.headerLogoAssetUuid
    ? `/api/workspace-assets/${workspace.headerLogoAssetUuid}/content`
    : null;
  const logoSrc = useProtectedImageUrl(logoUrl);
  const logoPosition = workspace?.headerLogoPosition ?? 'after';

  return (
    <>
      <div className="home-header-start">
        <Link to="/" className="home-header-wordmark">
          {headerWordmark}
        </Link>
        <div className="home-header-workspace-group">
          {logoSrc && logoPosition === 'before' && (
            <img src={logoSrc} alt="" className="home-header-logo" aria-hidden="true" />
          )}
          {identity !== undefined ? (
            <span className="home-header-identity">{identityLabel}</span>
          ) : (
            <WorkspaceSelector
              workspaces={availableWorkspaces}
              activeWorkspaceUuid={workspace?.uuid ?? null}
              onSelect={switchWorkspace}
            />
          )}
          {logoSrc && logoPosition === 'after' && (
            <img src={logoSrc} alt="" className="home-header-logo" aria-hidden="true" />
          )}
        </div>
        {showBadge && <LiveMeetingBadge />}
      </div>
      <div className="home-header-end">
        {showModuleSelector && (
          <ModuleSelector
            modules={modules}
            activeModuleKey={activeModuleKey}
            onSelect={setActiveModuleKey}
          />
        )}
        {import.meta.env.VITE_SANDBOX_TOOLS_ENABLED === 'true' && dealUuid && (
          <button
            type="button"
            className="dev-logs-btn"
            disabled={fillingSampleData}
            onClick={() => void handleFillSampleData()}
          >
            {fillingSampleData ? 'filling…' : 'fill sample'}
          </button>
        )}
        {import.meta.env.DEV && (
          <a href="/dev/logs" target="_blank" rel="noopener noreferrer" className="dev-logs-btn">
            logs
          </a>
        )}
        <NotificationBell />
        <UserMenu />
      </div>
    </>
  );
}
